/**
 * Hook de Monitoramento para Componentes React
 * Integra o sistema de monitoramento com componentes React
 */

import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext.jsx';

export const useProductionMonitoring = (componentName) => {
  const renderCountRef = useRef(0);
  const mountTimeRef = useRef(null);
  const { currentUser } = useAuth();

  useEffect(() => {
    mountTimeRef.current = performance.now();
    renderCountRef.current = 0;

    // Log do mount do componente
    if (window.productionMonitor) {
      window.productionMonitor.addCustomMetric('component_lifecycle', {
        type: 'mount',
        component: componentName,
        userId: currentUser?.uid || 'anonymous'
      });
    }

    return () => {
      // Log do unmount do componente
      if (window.productionMonitor && mountTimeRef.current) {
        const lifeTime = performance.now() - mountTimeRef.current;
        window.productionMonitor.addCustomMetric('component_lifecycle', {
          type: 'unmount',
          component: componentName,
          lifeTime: lifeTime,
          renderCount: renderCountRef.current,
          userId: currentUser?.uid || 'anonymous'
        });
      }
    };
  }, [componentName, currentUser?.uid]);

  useEffect(() => {
    renderCountRef.current += 1;

    // Log de re-renders frequentes (possível problema de performance)
    if (renderCountRef.current > 10 && window.productionMonitor) {
      window.productionMonitor.addCustomMetric('performance_warning', {
        type: 'excessive_renders',
        component: componentName,
        renderCount: renderCountRef.current,
        userId: currentUser?.uid || 'anonymous'
      });
    }
  });

  const logUserAction = useCallback((action, data = {}) => {
    if (window.productionMonitor) {
      window.productionMonitor.addCustomMetric('user_actions', {
        action,
        component: componentName,
        userId: currentUser?.uid || 'anonymous',
        ...data
      });
    }
  }, [componentName, currentUser?.uid]);

  const logError = useCallback((error, context = {}) => {
    if (window.productionMonitor) {
      window.productionMonitor.logError({
        type: 'component_error',
        message: error.message,
        stack: error.stack,
        component: componentName,
        userId: currentUser?.uid || 'anonymous',
        context: context,
        timestamp: new Date().toISOString(),
        url: window.location.href
      });
    }
  }, [componentName, currentUser?.uid]);

  const logPerformance = useCallback((operation, duration, data = {}) => {
    if (window.productionMonitor) {
      window.productionMonitor.addCustomMetric('component_performance', {
        operation,
        duration,
        component: componentName,
        userId: currentUser?.uid || 'anonymous',
        ...data
      });
    }
  }, [componentName, currentUser?.uid]);

  return {
    logUserAction,
    logError,
    logPerformance
  };
};

// Hook para monitorar operações assíncronas
export const useAsyncMonitoring = () => {
  const { currentUser } = useAuth();

  const monitorAsync = useCallback(async (operation, asyncFn, context = {}) => {
    const startTime = performance.now();
    let result, error;

    try {
      result = await asyncFn();
      const duration = performance.now() - startTime;

      if (window.productionMonitor) {
        window.productionMonitor.addCustomMetric('async_operations', {
          operation,
          duration,
          success: true,
          userId: currentUser?.uid || 'anonymous',
          ...context
        });
      }

      return result;
    } catch (err) {
      const duration = performance.now() - startTime;
      error = err;

      if (window.productionMonitor) {
        window.productionMonitor.addCustomMetric('async_operations', {
          operation,
          duration,
          success: false,
          error: err.message,
          userId: currentUser?.uid || 'anonymous',
          ...context
        });

        window.productionMonitor.logError({
          type: 'async_operation_error',
          message: err.message,
          stack: err.stack,
          operation,
          userId: currentUser?.uid || 'anonymous',
          context,
          timestamp: new Date().toISOString(),
          url: window.location.href
        });
      }

      throw error;
    }
  }, [currentUser?.uid]);

  return { monitorAsync };
};

// Hook para monitorar navegação
export const useNavigationMonitoring = () => {
  const { currentUser } = useAuth();

  useEffect(() => {
    const logNavigation = (type, path) => {
      if (window.productionMonitor) {
        window.productionMonitor.addCustomMetric('navigation', {
          type,
          path,
          userId: currentUser?.uid || 'anonymous',
          referrer: document.referrer,
          timestamp: new Date().toISOString()
        });
      }
    };

    // Monitorar mudanças de rota
    const handleRouteChange = () => {
      logNavigation('route_change', window.location.pathname);
    };

    // Monitorar cliques em links
    const handleLinkClick = (event) => {
      const target = event.target.closest('a');
      if (target && target.href) {
        logNavigation('link_click', target.href);
      }
    };

    window.addEventListener('popstate', handleRouteChange);
    document.addEventListener('click', handleLinkClick);

    return () => {
      window.removeEventListener('popstate', handleRouteChange);
      document.removeEventListener('click', handleLinkClick);
    };
  }, [currentUser?.uid]);
};

export default {
  useProductionMonitoring,
  useAsyncMonitoring,
  useNavigationMonitoring
};
